import * as React from 'react'
import withHydrationOnDemand from 'react-hydration-on-demand'
import { GetServerSidePropsContext } from 'next'
import getConfig from 'next/config'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import { vsprintf } from 'sprintf-js'

import { SearchResultsArgs as AuroraSearchResultArgs } from '@thg-commerce/enterprise-aurora-search/resolvers/SearchResults/resolver'
import {
  pageTTLConfig,
  PageType as CachePageType,
} from '@thg-commerce/enterprise-cache'
import { SponsoredAdsType } from '@thg-commerce/enterprise-components/SponsoredAds/queryBuilders'
import {
  EnterpriseNextPage,
  getSafeUrlParametersFromReq,
  i18n,
  loggerProvider,
  PageWrapper,
  useLogger,
  useSiteConfig,
  withCacheConfiguration,
} from '@thg-commerce/enterprise-core'
import { PageType } from '@thg-commerce/enterprise-core/src/App/types'
import { loadConfiguration } from '@thg-commerce/enterprise-core/src/ConfigurationLoader/ConfigurationLoader'
import { NextConfig } from '@thg-commerce/enterprise-core/src/ConfigurationLoader/types'
import { createApolloWithRequest } from '@thg-commerce/enterprise-core/src/Factory/apollo'
import { createGraphQLClient } from '@thg-commerce/enterprise-core/src/Factory/graphql'
import { FullHeaderLayout } from '@thg-commerce/enterprise-core/src/Layouts'
import {
  EnterpriseRequest,
  EnterpriseResponse,
} from '@thg-commerce/enterprise-core/src/Server/types'
import { addToDataLayer } from '@thg-commerce/enterprise-metrics'
import { Resolvers } from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers'
import {
  ComponentName,
  ComponentWidgetsData,
  ComponentWidgetsVariables,
} from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Query/ComponentWidgets'
import { SearchResultsArgs } from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Query/Content/SearchResults'
import { DataLayerProducts } from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Query/Page/ProductListProducts'
import {
  Country,
  Currency,
  FacetInput,
  Feature,
  Maybe,
  Product,
  ProductList,
  ProductSort,
  Widget,
} from '@thg-commerce/enterprise-network/src/generated/graphql'
import { ColourSwatch } from '@thg-commerce/enterprise-network/types/enterpriseTypes'
import { productOptionsHeaderOverrides } from '@thg-commerce/enterprise-product-options/src/headerOverrides'
import {
  ChangeType,
  FacetType,
  PageOptions,
  ProductListPage,
  ProductListPageProps,
} from '@thg-commerce/enterprise-widget-product-list-page'
import { facetsCallbacks } from '@thg-commerce/enterprise-widget-product-list-page/src/dataTracking'
import { getFacetUrlFromInput } from '@thg-commerce/enterprise-widget-product-list-page/src/Facets/utils'
import { getFacetInputFromUrl } from '@thg-commerce/enterprise-widgets/src/ProductListWidgetRenderer/utils'
import { WidgetRendererProps } from '@thg-commerce/enterprise-widgets/src/WidgetRenderer/WidgetRenderer'
import { PlatformMessage } from '@thg-commerce/gravity-elements'
import { GridItem } from '@thg-commerce/gravity-system'

import { SearchResults as SEARCH_RESULTS_QUERY_AURORA } from '../../graphql/SearchResults.graphql'
import { SearchResults as SEARCH_RESULTS_QUERY } from '../../src/graphql/SearchResults.graphql'
import { NoSearchResultsFound } from '../../src/renderer/SearchResults/NoSearchResultsFound'

const WidgetRenderer = dynamic<WidgetRendererProps>(() =>
  import('@thg-commerce/enterprise-widgets').then((mod) => mod.WidgetRenderer),
)
const WidgetRendererComponent = withHydrationOnDemand({
  on: ['visible'],
})(WidgetRenderer)

export type SearchResultsProps = {
  data?: {
    searchResults: Omit<ProductList, 'products'>
  }
  componentWidgets?: Maybe<Widget[]>
  products: ProductListPageProps['products']
  dataLayerProducts: DataLayerProducts
  query: string
  itemsPerPage: number
  facets: FacetInput[]
  pageNumber: number
  sortOrder: ProductSort
  wishlistEnabled?: boolean
  dataLayerMap: {
    [key: string]: {
      [key: string]: {
        propertyKey: string
        label: string
      }
    }
  }
}

export const SEARCH_RESULTS_PER_PAGE = 45
// @TODO: [CSBOM-325] Pull these values from content keys
const IFP_FILTERS = [
  'en_brand_content',
  'en_hbg_sizeFacet_content',
  'en_hbg_baseColour_content',
  'en_hbg_type_content',
]

const undefinedProductSearch = (query) => {
  return (
    <ProductListPage
      changeType={ChangeType.NONE}
      itemsPerPage={SEARCH_RESULTS_PER_PAGE}
      totalItems={0}
      loading={true}
      sortTypes={[
        {
          type: ProductSort.Relevance,
          displayText: '',
        },
      ]}
      pageOptions={{
        sort: ProductSort.Relevance,
        pageNumber: 1,
        facets: [],
        searchQuery: query,
      }}
      onInputChange={() => {}}
      facets={[]}
      products={[]}
      sponsoredAdsType={SponsoredAdsType.Search}
      dataLayerMap={{}}
    />
  )
}

const undefinedSearchResults = (query, componentWidgets) => {
  return (
    <React.Fragment>
      <NoSearchResultsFound query={query} />
      <WidgetRendererComponent widgets={componentWidgets || []} />
    </React.Fragment>
  )
}

const SearchResults: EnterpriseNextPage<SearchResultsProps> = (props) => {
  const logger = useLogger()
  const { enableIndexableFacetsPagination } = useSiteConfig()

  const sortTypes = [
    {
      type: ProductSort.Relevance,
      displayText: i18n('product.list.sort.relevance'),
    },
    {
      type: ProductSort.Popularity,
      displayText: i18n('product.list.sort.popularity'),
    },
    {
      type: ProductSort.PriceLowToHigh,
      displayText: i18n('product.list.sort.pricelowtohigh'),
    },
    {
      type: ProductSort.PriceHighToLow,
      displayText: i18n('product.list.sort.pricehightolow'),
    },
    {
      type: ProductSort.NameAlphabetically,
      displayText: i18n('product.list.sort.namealphabetically'),
    },
    {
      type: ProductSort.NewestToOldest,
      displayText: i18n('product.list.sort.newesttooldest'),
    },
    {
      type: ProductSort.DiscountPercentageHighToLow,
      displayText: i18n('product.list.sort.discountpercentagehightolow'),
    },
  ]

  const i18nText = {
    searchTitle: i18n('content.search.title'),
    siteTitleEnd: i18n('titles.end.text'),
    errorMessage: i18n('product.list.error.text'),
    resultsFoundTitle: i18n('content.searchresults.title.resultsfound'),
    correctedQueryTitle: i18n('content.searchresults.title.correctedquery'),
  }

  React.useEffect(() => {
    addToDataLayer({ key: 'siteSearchTerm', value: props.query })
  }, [props.query])

  if (props.data === undefined) {
    return undefinedProductSearch(props.query)
  }

  if (!props.products) {
    logger.error(
      `SearchResultsRenderer: Failed to load data for search results`,
    )
    facetsCallbacks.errorOnLoad()
    return (
      <PlatformMessage type="error" text={i18nText.errorMessage} centerText />
    )
  }

  if (
    props.data === undefined ||
    (props.data?.searchResults &&
      (props.data.searchResults.total === 0 || props.products.length === 0))
  ) {
    return undefinedSearchResults(props.query, props.componentWidgets)
  }

  const searchData = props.data?.searchResults ?? {
    total: 0,
    hasMore: false,
    facets: [],
    products: [],
  }

  const totalItems = searchData
    ? searchData?.correctedQuery
      ? props.products.length
      : searchData.total
    : 0

  const title = props.data?.searchResults?.correctedQuery
    ? vsprintf(i18nText.correctedQueryTitle, [
        props.query,
        props.data.searchResults.correctedQuery,
      ])
    : vsprintf(i18nText.resultsFoundTitle, [props.query])

  const onInputChangeCallback = (newInput: PageOptions) => {
    const url = new URL(window.location.href)

    if (newInput.pageNumber) {
      url.searchParams.set('pageNumber', newInput.pageNumber.toString())
    }

    if (newInput.sort) {
      url.searchParams.set('sortOrder', newInput.sort.toString())
      url.searchParams.set('pageNumber', '1')
    }

    if (newInput.facets) {
      url.searchParams.set(
        'facetFilters',
        getFacetUrlFromInput(newInput.facets),
      )
      if (enableIndexableFacetsPagination) {
        if (!newInput.facets.length && url.searchParams.has('IFP')) {
          url.searchParams.delete('IFP')
        } else {
          const setIFPfilter = IFP_FILTERS.some((filter) =>
            url.search.includes(filter),
          )
          setIFPfilter
            ? url.searchParams.set('IFP', 'true')
            : url.searchParams.set('IFP', 'false')
        }
      }
      url.searchParams.set('pageNumber', '1')
    }

    window.location.assign(url.toString())
  }
  const sessionStorage = typeof window !== 'undefined' && window.sessionStorage

  const productListWidgetProps: ProductListPageProps = {
    title,
    sortTypes,
    totalItems,
    itemsPerPage: props.itemsPerPage,
    products: props.products || [],
    dataLayerProducts: props.dataLayerProducts,
    onInputChange: onInputChangeCallback,
    pageOptions: {
      facets: props.facets,
      pageNumber: props.pageNumber,
      sort: props.sortOrder,
      searchQuery: props.query,
    },
    facets: searchData ? (searchData.facets as FacetType[]) : [],
    sponsoredAdsType: SponsoredAdsType.Search,
    setSingleColumn: (item, value) =>
      sessionStorage && sessionStorage.setItem(item, value),
    dataLayerMap: props.dataLayerMap,
  }

  return (
    <React.Fragment>
      <Head>
        <title>
          {vsprintf(i18nText.searchTitle, [props.query])}{' '}
          {i18nText.siteTitleEnd}
        </title>
      </Head>
      <PageWrapper compactMargin={true} sendPageVisit={true}>
        <GridItem colSpan={12}>
          <ProductListPage {...productListWidgetProps} />
        </GridItem>
      </PageWrapper>
    </React.Fragment>
  )
}

SearchResults.Layout = FullHeaderLayout

SearchResults.pageType = PageType.SEARCH

export const getServerSideProps = withCacheConfiguration(
  async (ctx: GetServerSidePropsContext) => {
    const nextConfig = getConfig() as NextConfig
    const request = ctx.req as EnterpriseRequest
    const response = ctx.res as EnterpriseResponse
    const config = await loadConfiguration(nextConfig, request)

    const { publicRuntimeConfig, dataLayerMap = {} } = config
    const { brand, subsite, originUrl } = publicRuntimeConfig.siteDefinition
    const logger = loggerProvider({ brand, subsite, originUrl })
    const { siteDefinition, siteConfig } = publicRuntimeConfig

    const horizonFeatures = request.horizonFeatures
    const shippingDestination = (request.config.sessionSettings
      ?.shippingDestination ||
      siteDefinition.defaultSessionSettings.shippingDestination) as Country
    const currency = (request.config.sessionSettings?.currency ||
      siteDefinition.defaultSessionSettings.currency) as Currency

    const vipPriceEnabled =
      siteConfig.enableVipPrice &&
      (request.horizonFeatures || []).includes(Feature.VipPricingEnabled)

    const productContentKeys = config.contentKeys?.productBlock
    const tagKeys =
      productContentKeys && siteConfig?.productTagsKeys
        ? productContentKeys.filter((contentKey) =>
            siteConfig.productTagsKeys?.includes(contentKey),
          )
        : []

    const {
      enableWishlists,
      enableWishlistsGlobal,
      hideProductListWishlists,
      productItemsPerPage,
      hasImagesCarouselOnMobilePLP,
      enableAuroraSearch,
      enableHeroProductImageTag,
      enableProductPersonalisation = false,
      enablePricePerUnit = false,
    } = siteConfig

    const {
      pageNumber: pageNumberUrlParam,
      facetFilters,
      sortOrder: sortOrderUrlParam,
      search,
    } = getSafeUrlParametersFromReq<{
      pageNumber?: string
      facetFilters?: string
      sortOrder?: string
      search?: string
    }>(ctx, ['pageNumber', 'facetFilters', 'sortOrder', 'search'])

    const facets = getFacetInputFromUrl(facetFilters ? facetFilters : '')

    const pageNumber =
      pageNumberUrlParam && parseInt(pageNumberUrlParam, 10) >= 1
        ? parseInt(pageNumberUrlParam, 10)
        : 1
    const sortOrder =
      Object.values(ProductSort).find((sort) => sort === sortOrderUrlParam) ||
      ProductSort.Relevance

    const query = search && search !== 'null' ? search : ''

    const wishlistEnabled =
      (enableWishlists &&
        enableWishlistsGlobal &&
        !hideProductListWishlists &&
        horizonFeatures?.includes(Feature.Wishlist)) ||
      false

    const clickAndCollectEnabled = horizonFeatures?.includes(
      Feature.ClickAndCollect,
    )

    const imageLimit = hasImagesCarouselOnMobilePLP ? 10 : 2

    const itemsPerPage = productItemsPerPage || SEARCH_RESULTS_PER_PAGE

    const enableAurora =
      process.env.ENABLE_AURORA === 'true' &&
      (enableAuroraSearch ||
        request.headers['x-enterprise-enable-aurora'] === '1')

    const {
      enableAuroraProductOptions,
      preselectVariant,
    } = productOptionsHeaderOverrides({
      siteConfig,
      headers: request.headers,
    })

    if (enableAurora) {
      const client = createGraphQLClient(config, request)

      try {
        const options = {
          currency,
          facets,
          shippingDestination,
          limit: itemsPerPage,
          offset: pageNumber > 1 ? itemsPerPage * (pageNumber - 1) : 0,
          sort: sortOrder,
        }

        const { data } = await client.query<
          {
            searchResults: Omit<ProductList, 'products'> & {
              products: (Product & { colourSwatches: ColourSwatch[] })[]
              varyHeaders: string[]
              dataLayerProducts: DataLayerProducts
              rayId: string
              widgets: ComponentWidgetsData
            }
          },
          AuroraSearchResultArgs
        >(SEARCH_RESULTS_QUERY_AURORA, {
          wishlistEnabled,
          vipPriceEnabled,
          query,
          options,
          productContentKeys,
          tagKeys,
          enablePricePerUnit,
          enableAuroraProductOptions,
          preselectVariant,
          enableProductPersonalisation,
          enableHeroProductImageTag: enableHeroProductImageTag || false,
          currency: currency as Currency,
          shippingDestination: shippingDestination as Country,
          clickAndCollectEnabled: clickAndCollectEnabled ?? false,
          limitImages: imageLimit,
        })

        if (data) {
          const {
            products,
            dataLayerProducts,
            widgets,
            rayId = null,
          } = data.searchResults

          const productBlocks = products.map((product) => {
            const selectedOptions = product.sortedOptionKeys.reduce(
              (accumulator, optionKey, index) => {
                const choiceKey = product.selectedChoices[index]
                if (optionKey && choiceKey) {
                  accumulator[optionKey] = choiceKey
                }

                return accumulator
              },
              {},
            )

            product.selectedOptions = selectedOptions
            return product
          })

          return {
            props: {
              rayId,
              query,
              itemsPerPage,
              facets,
              pageNumber,
              sortOrder,
              wishlistEnabled,
              data,
              dataLayerProducts,
              dataLayerMap,
              products: productBlocks,
              componentWidgets: widgets || null,
            },
            cache: {
              ...(pageTTLConfig(CachePageType.SEARCH) || {
                ttl: 300,
                grace: 86400,
              }),
              vary: data.searchResults.varyHeaders,
            },
          }
        }
      } catch (e) {
        const error = e as Error
        logger.warn(
          `Failed to fetch search results with error ${error.message}`,
          {
            stack: error.stack,
          },
        )
      }
    } else {
      const { allowOverrides, allowSitePropertyOverrides } = request.config

      const apollo = createApolloWithRequest(request, config, true)

      const productContentKeys = config.contentKeys?.productBlock
      apollo.addResolvers(
        Resolvers(
          apollo,
          publicRuntimeConfig,
          allowOverrides,
          allowSitePropertyOverrides,
          request.config.enableVary,
          productContentKeys,
        ),
      )

      try {
        const { data } = await apollo.query<
          {
            searchResults: Omit<ProductList, 'products'> & {
              products: (Product & { colourSwatches: ColourSwatch[] })[]
              varyHeaders: string[]
              dataLayerProducts: DataLayerProducts
            }
            componentWidgets: ComponentWidgetsData
          },
          SearchResultsArgs & ComponentWidgetsVariables
        >({
          query: SEARCH_RESULTS_QUERY,
          variables: {
            wishlistEnabled,
            query,
            vipPriceEnabled,
            productContentKeys,
            enableHeroProductImageTag: enableHeroProductImageTag || false,
            name: ComponentName.NORESULTS,
            currency: currency as Currency,
            shippingDestination: shippingDestination as Country,
            clickAndCollectEnabled: clickAndCollectEnabled ?? false,
            limitImages: imageLimit,
            options: {
              currency,
              facets,
              shippingDestination,
              limit: itemsPerPage,
              offset: pageNumber > 1 ? itemsPerPage * (pageNumber - 1) : 0,
              sort: sortOrder,
            },
          },
        })

        if (data) {
          const { products, dataLayerProducts } = data.searchResults
          return {
            props: {
              products,
              dataLayerProducts,
              query,
              itemsPerPage,
              facets,
              pageNumber,
              sortOrder,
              wishlistEnabled,
              data,
              dataLayerMap,
              componentWidgets: data.componentWidgets,
            },
            cache: {
              ...(pageTTLConfig(CachePageType.SEARCH) || {
                ttl: 300,
                grace: 86400,
              }),
              vary: data.searchResults.varyHeaders,
            },
          }
        }
      } catch (e) {
        const error = e as Error
        logger.warn(
          `Failed to fetch search results with error ${error.message}`,
          {
            stack: error.stack,
          },
        )
      }
    }

    response && (response.statusCode = 404)

    return {
      props: {
        query,
        pageNumber,
        sortOrder,
        itemsPerPage: 0,
        facets: [],
      },
    }
  },
)

export default SearchResults
